// The organization's money in Stripe (D56, D58, APS-468), from the API's mirror: the balance, the
// live subscription and the last invoices with their refunds and NFS-e. Staff credit the balance
// and refund an invoice from here (form windows with the Motivo); the subscription's
// cancellation is in the Danger zone (CancelRows). Every action needs a recent sign-in and is in
// both audit logs.
import { Section, Status } from '@apsio/ui'
import { Fragment, useId, useState } from 'react'
import {
  cancelSubscription,
  creditBalance,
  type NfseStatus,
  type OrgDetail,
  refundInvoice,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DangerRow, DataTable, IdText, RowMenu, StatusWord } from '../components/kit'
import { Empty, ErrorState } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'
import { PLAN_LABEL } from './pills'

/** Stripe's subscription and invoice states in the staff's words; anything else as Stripe says it. */
const STATE_LABEL: Record<string, MessageId> = {
  active: 'org.billing.state.active',
  trialing: 'org.billing.state.trialing',
  past_due: 'org.billing.state.pastDue',
  unpaid: 'org.billing.state.unpaid',
  incomplete: 'org.billing.state.incomplete',
  open: 'org.billing.state.open',
  draft: 'org.billing.state.draft',
  uncollectible: 'org.billing.state.uncollectible',
  void: 'org.billing.state.void',
}
const stateLabel = (t: (id: MessageId) => string, state: string) => {
  const id = STATE_LABEL[state]
  return id ? t(id) : state
}

type Billing = NonNullable<OrgDetail['billing']>
type Invoice = Billing['invoices'][number]

/** One request key per opened form: a retried submit changes nothing (the API's no-op). */
function useRequestKey(): [string, () => void] {
  const [key, setKey] = useState(() => crypto.randomUUID())
  return [key, () => setKey(crypto.randomUUID())]
}

/** Money typed by staff ("150", "150,50", "1.234,56", "150.5") as minor units; null if not. */
export function centsOf(text: string): number | null {
  let plain = text.replace(/\s/g, '')
  // A comma is the decimal mark (pt-BR): dots before it group thousands.
  if (plain.includes(',')) plain = plain.replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.')
  else if (/^\d{1,3}(\.\d{3})+$/.test(plain)) plain = plain.replace(/\./g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(plain)) return null
  const [units = '0', fraction = ''] = plain.split('.')
  const cents = Number(units) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null
}

export function OrgBilling({ org }: { org: OrgDetail }) {
  const { t } = useI18n()
  const [crediting, setCrediting] = useState(false)
  const b = org.billing
  // The section's action in its head, as every section's (Mosaic); the form opens under it.
  const right = b ? (
    <button
      type="button"
      className="btn ghost sm"
      aria-expanded={crediting}
      onClick={() => setCrediting(!crediting)}
    >
      {t('org.billing.creditAction')}
    </button>
  ) : null
  return (
    <Section title={t('org.billing.title')} right={right}>
      {b ? (
        <BillingBody
          org={org}
          billing={b}
          crediting={crediting}
          onCredited={() => setCrediting(false)}
        />
      ) : (
        <Empty title={t('org.billing.none')} />
      )}
    </Section>
  )
}

/** Subscription states that need staff's eye, as a warn status word in the facts. */
const WARN_STATES = new Set(['past_due', 'unpaid', 'incomplete'])

function BillingBody({
  org,
  billing: b,
  crediting,
  onCredited,
}: {
  org: OrgDetail
  billing: Billing
  crediting: boolean
  onCredited: () => void
}) {
  const { t, date } = useI18n()
  const { money } = useFormat()
  const refresh = [['org', org.id]]
  const credit = useAdminMutation(creditBalance, refresh)
  const [key, nextKey] = useRequestKey()
  const ids = { amount: useId() }
  const s = b.subscription
  return (
    <>
      <ActionForm
        open={crediting}
        onClose={onCredited}
        label={t('org.billing.creditAction')}
        confirm={t('org.billing.creditConfirm')}
        reasonHint={t('org.billing.creditHint', { currency: b.currency.toUpperCase() })}
        validate={(form) =>
          centsOf(String(form.get('amount') ?? '')) === null ? t('org.billing.badAmount') : null
        }
        onSubmit={async (reason, form) => {
          const amountCents = centsOf(String(form.get('amount') ?? '')) as number
          await credit.mutateAsync({ orgId: org.id, amountCents, reason, requestKey: key })
          nextKey()
        }}
      >
        <div className="field-d">
          <label className="lbl" htmlFor={ids.amount}>
            {t('org.billing.amount', { currency: b.currency.toUpperCase() })}
          </label>
          <input
            id={ids.amount}
            name="amount"
            className="input mono num-field"
            inputMode="decimal"
            autoComplete="off"
            required
          />
        </div>
      </ActionForm>
      <dl className="kv">
        <dt>{t('org.billing.balance')}</dt>
        <dd>
          {b.balance_cents < 0
            ? t('org.billing.credit', { amount: money(-b.balance_cents, b.currency) })
            : b.balance_cents > 0
              ? t('org.billing.owed', { amount: money(b.balance_cents, b.currency) })
              : t('common.none')}
        </dd>
        <dt>{t('org.billing.subscription')}</dt>
        <dd>
          {s ? (
            <>
              {t(PLAN_LABEL[s.plan])} ·{' '}
              {WARN_STATES.has(s.status) ? (
                <Status s="miss" glyph="!" label={stateLabel(t, s.status)} />
              ) : (
                stateLabel(t, s.status)
              )}{' '}
              ·{' '}
              {t(s.cancel_at_period_end ? 'org.billing.subEnds' : 'org.billing.subRenews', {
                date: date(s.period_end),
              })}
            </>
          ) : (
            t('org.billing.noSubscription')
          )}
        </dd>
      </dl>
      {b.invoices.length === 0 ? (
        <Empty title={t('org.billing.noInvoices')} />
      ) : (
        <Invoices org={org} invoices={b.invoices} />
      )}
    </>
  )
}

function Invoices({ org, invoices }: { org: OrgDetail; invoices: Invoice[] }) {
  const { t, date } = useI18n()
  const { money } = useFormat()
  const [open, setOpen] = useState<string | null>(null)
  return (
    <DataTable label={t('org.billing.invoices')} minWidth={760}>
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('org.billing.col.invoice')}
          </th>
          <th scope="col">{t('org.billing.col.date')}</th>
          <th className="num" scope="col">
            {t('org.billing.col.paid')}
          </th>
          <th className="num" scope="col">
            {t('org.billing.col.refunded')}
          </th>
          <th scope="col">{t('org.billing.col.nfse')}</th>
          <th className="last act" scope="col">
            <span className="vh">{t('org.billing.col.action')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {invoices.map((i) => {
          const left = i.amount_paid - i.refunded_cents
          const name = i.number ?? i.id
          return (
            <Fragment key={i.id}>
              <tr>
                <td className="f mono" translate="no">
                  {i.number ?? <IdText id={i.id} head={7} />}
                </td>
                <td>{date(i.paid_at ?? i.created_at)}</td>
                <td className="num">
                  {i.status === 'paid' ? (
                    money(i.amount_paid, i.currency)
                  ) : (
                    <span className="dim">{stateLabel(t, i.status)}</span>
                  )}
                </td>
                <td className="num">
                  {i.refunded_cents > 0 ? money(i.refunded_cents, i.currency) : t('common.none')}
                </td>
                <td>
                  {i.nfse_status ? (
                    <StatusWord kind="nfse" status={i.nfse_status as NfseStatus} />
                  ) : (
                    t('common.none')
                  )}
                </td>
                <td className="last act">
                  {i.status === 'paid' && left > 0 ? (
                    <RowMenu
                      label={t('org.billing.invoiceActions', { invoice: name })}
                      items={[
                        { label: t('org.billing.refundAction'), onSelect: () => setOpen(i.id) },
                      ]}
                    />
                  ) : null}
                </td>
              </tr>
              {open === i.id ? (
                <tr className="edit money-edit">
                  <td colSpan={6}>
                    <RefundForm org={org} invoice={i} left={left} onDone={() => setOpen(null)} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          )
        })}
      </tbody>
    </DataTable>
  )
}

/** The refund's amount field: undefined when empty (all that is left), null when not money. */
const amountOf = (form: FormData) => {
  const typed = String(form.get('amount') ?? '').trim()
  return typed === '' ? undefined : centsOf(typed)
}

/** A refund of the invoice: empty, all that is left; else the amount typed, up to that. */
function RefundForm({
  org,
  invoice,
  left,
  onDone,
}: {
  org: OrgDetail
  invoice: Invoice
  left: number
  onDone: () => void
}) {
  const { t } = useI18n()
  const { money } = useFormat()
  const refund = useAdminMutation(refundInvoice, [['org', org.id]])
  const [key] = useRequestKey()
  const id = useId()
  return (
    <ActionForm
      open
      onClose={onDone}
      label={t('org.billing.refundTitle', { invoice: invoice.number ?? invoice.id })}
      confirm={t('org.billing.refundConfirm')}
      danger
      reasonHint={(
        ['org.billing.refundHint1', 'org.billing.refundHint2', 'org.billing.refundHint3'] as const
      ).map((id) => (
        <span key={id} className="hint-line">
          {t(id)}
        </span>
      ))}
      validate={(form) => {
        const amountCents = amountOf(form)
        return amountCents === null || (amountCents !== undefined && amountCents > left)
          ? t('org.billing.badRefund', { left: money(left, invoice.currency) })
          : null
      }}
      onSubmit={async (reason, form) => {
        const amountCents = amountOf(form) ?? undefined
        await refund.mutateAsync({
          orgId: org.id,
          invoiceId: invoice.id,
          amountCents,
          reason,
          requestKey: key,
        })
      }}
    >
      <div className="field-d">
        <label className="lbl" htmlFor={id}>
          {t('org.billing.refundAmount', { currency: invoice.currency.toUpperCase() })}
        </label>
        <input
          id={id}
          name="amount"
          className="input mono num-field"
          inputMode="decimal"
          autoComplete="off"
          placeholder={money(left, invoice.currency)}
        />
        <span className="hint">
          {t('org.billing.refundLeft', { left: money(left, invoice.currency) })}
        </span>
      </div>
    </ActionForm>
  )
}

/**
 * The Danger zone's rows for a live subscription: cancel at the period's end (it runs and bills
 * until then), or now (a last invoice for the usage so far). Nothing paid is refunded.
 */
export function CancelRows({ org }: { org: OrgDetail }) {
  const { t, date } = useI18n()
  const cancel = useAdminMutation(cancelSubscription, [['org', org.id]])
  const [key, nextKey] = useRequestKey()
  const s = org.billing?.subscription
  if (!s) return null
  const run = (when: 'period_end' | 'now') => (reason: string) =>
    cancel.mutate({ orgId: org.id, when, reason, requestKey: key }, { onSuccess: nextKey })
  const error = cancel.error ? <ErrorState error={cancel.error} /> : null
  const ends = date(s.period_end)
  return (
    <>
      {s.cancel_at_period_end ? null : (
        <DangerRow
          title={t('org.billing.cancelEndTitle')}
          body={t('org.billing.cancelEndBody', { date: ends })}
          action={t('org.billing.cancelEndAction')}
          confirmTitle={t('org.billing.cancelEndConfirmTitle')}
          confirmBody={t('org.billing.cancelEndConfirmBody', { name: org.name, date: ends })}
          confirmLabel={t('org.billing.cancelEndAction')}
          dismissLabel={t('org.billing.keep')}
          busy={cancel.isPending}
          error={cancel.variables?.when === 'period_end' ? error : null}
          onConfirm={run('period_end')}
        />
      )}
      <DangerRow
        title={t('org.billing.cancelNowTitle')}
        body={t('org.billing.cancelNowBody')}
        action={t('org.billing.cancelNowAction')}
        confirmTitle={t('org.billing.cancelNowConfirmTitle')}
        confirmBody={t('org.billing.cancelNowConfirmBody', { name: org.name })}
        confirmLabel={t('org.billing.cancelNowAction')}
        dismissLabel={t('org.billing.keep')}
        typed={org.name}
        busy={cancel.isPending}
        error={cancel.variables?.when === 'now' ? error : null}
        onConfirm={run('now')}
      />
    </>
  )
}
